import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import * as hospitalAdminApi from './api.client'
import { ManagedHospital } from './types'
import {
  getManagedHospitals as getStoredManagedHospitals,
  getSelectedHospitalId as getStoredSelectedHospitalId,
  getUserProfile,
  setHospitalAdmin,
  setHospitalSynchronizedAt,
  setManagedHospitals as setStoredManagedHospitals,
  setSelectedHospitalId as setStoredSelectedHospitalId,
} from '../user/auth'
import { UserRole } from '../user/types'

export type HospitalAdminContextValue = {
  managedHospitals: ManagedHospital[]
  selectedHospital: ManagedHospital | null
  selectHospital: (hospital: ManagedHospital) => Promise<void>
  loading: boolean
  requiresHospitalSelection: boolean
}

const HospitalAdminContext = createContext<HospitalAdminContextValue | null>(
  null
)

const loadHospitalSyncTimestamp = async (hospitalId: number) => {
  try {
    const sync = await hospitalAdminApi.getHospitalSynchronizedAt(hospitalId)
    const ms = Number(sync.sincronizedAt)

    if (Number.isFinite(ms)) {
      setHospitalSynchronizedAt(ms)
    }
  } catch (error) {
    console.error(error)
  }
}

const applySelectedHospital = async (hospital: ManagedHospital) => {
  setStoredSelectedHospitalId(hospital.hospitalId)
  setHospitalAdmin(hospital.hospital)
  await loadHospitalSyncTimestamp(hospital.hospitalId)
}

export const HospitalAdminProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const isHospitalAdmin = getUserProfile() === UserRole.HOSPITAL_ADMIN
  const [managedHospitals, setManagedHospitals] = useState<ManagedHospital[]>(
    () => (isHospitalAdmin ? getStoredManagedHospitals() : [])
  )
  const [selectedHospital, setSelectedHospital] =
    useState<ManagedHospital | null>(() => {
      if (!isHospitalAdmin) {
        return null
      }

      const storedHospitalId = getStoredSelectedHospitalId()
      const storedHospitals = getStoredManagedHospitals()

      if (storedHospitalId == null) {
        return storedHospitals.length > 0 ? storedHospitals[0] : null
      }

      return (
        storedHospitals.find(
          (hospital) => hospital.hospitalId === storedHospitalId
        ) ?? null
      )
    })
  const [loading, setLoading] = useState(isHospitalAdmin)

  const selectHospital = useCallback(async (hospital: ManagedHospital) => {
    setSelectedHospital(hospital)
    await applySelectedHospital(hospital)
  }, [])

  useEffect(() => {
    if (!isHospitalAdmin) {
      setLoading(false)
      return
    }

    let cancelled = false

    const loadManagedHospitals = async () => {
      setLoading(true)

      try {
        const hospitals = await hospitalAdminApi.getManagedHospitals()

        if (cancelled) {
          return
        }

        setManagedHospitals(hospitals)
        setStoredManagedHospitals(hospitals)

        const storedHospitalId = getStoredSelectedHospitalId()
        const storedSelection = hospitals.find(
          (hospital) => hospital.hospitalId === storedHospitalId
        )

        if (storedSelection) {
          setSelectedHospital(storedSelection)
          await applySelectedHospital(storedSelection)
          return
        }

        if (hospitals.length > 0) {
          setSelectedHospital(hospitals[0])
          await applySelectedHospital(hospitals[0])
        }
      } catch (error) {
        console.error('Não foi possível carregar os hospitais administrados.', error)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadManagedHospitals()

    return () => {
      cancelled = true
    }
  }, [isHospitalAdmin])

  const value = useMemo(
    () => ({
      managedHospitals,
      selectedHospital,
      selectHospital,
      loading,
      requiresHospitalSelection:
        managedHospitals.length > 1 && selectedHospital == null,
    }),
    [
      managedHospitals,
      selectedHospital,
      selectHospital,
      loading,
    ]
  )

  return (
    <HospitalAdminContext.Provider value={value}>
      {children}
    </HospitalAdminContext.Provider>
  )
}

export const useHospitalAdmin = (): HospitalAdminContextValue => {
  const context = useContext(HospitalAdminContext)

  if (!context) {
    throw new Error(
      'useHospitalAdmin must be used within a HospitalAdminProvider'
    )
  }

  return context
}

export const useOptionalHospitalAdmin = (): HospitalAdminContextValue | null =>
  useContext(HospitalAdminContext)
